import { PERMISSIONS, type Permission } from './permissions.js';

export const ROLE_KEYS = {
  OWNER: 'owner',
  ADMIN: 'admin',
  EDITOR: 'editor',
  PUBLISHER: 'publisher',
  DEVELOPER: 'developer',
  BILLING_MANAGER: 'billing_manager',
  VIEWER: 'viewer',
} as const;

export type RoleKey = (typeof ROLE_KEYS)[keyof typeof ROLE_KEYS];

const READ_ONLY: Permission[] = [
  PERMISSIONS.ORG_MEMBERS_VIEW,
  PERMISSIONS.SITE_VIEW,
  PERMISSIONS.CONTENT_VIEW,
  PERMISSIONS.CONTENT_VERSIONS_VIEW,
  PERMISSIONS.MEDIA_VIEW,
  PERMISSIONS.LICENCES_VIEW,
];

const EDITOR: Permission[] = [
  ...READ_ONLY,
  PERMISSIONS.CONTENT_EDIT,
  PERMISSIONS.MEDIA_MANAGE,
];

const PUBLISHER: Permission[] = [
  ...EDITOR,
  PERMISSIONS.CONTENT_PUBLISH,
  PERMISSIONS.CONTENT_SCHEDULE,
  PERMISSIONS.CONTENT_VERSIONS_RESTORE,
];

const DEVELOPER: Permission[] = [
  ...PUBLISHER,
  PERMISSIONS.SITE_CREATE,
  PERMISSIONS.SITE_CONFIG_MANAGE,
  PERMISSIONS.SITE_ENVIRONMENTS_MANAGE,
  PERMISSIONS.SITE_DOMAINS_MANAGE,
  PERMISSIONS.CONTENT_MODELS_MANAGE,
  PERMISSIONS.API_KEYS_MANAGE,
  PERMISSIONS.WEBHOOKS_MANAGE,
];

const BILLING_MANAGER: Permission[] = [
  ...READ_ONLY,
  PERMISSIONS.BILLING_VIEW,
  PERMISSIONS.BILLING_MANAGE,
  PERMISSIONS.BILLING_INVOICES_DOWNLOAD,
];

const ADMIN: Permission[] = [
  ...DEVELOPER,
  ...BILLING_MANAGER,
  PERMISSIONS.ORG_SETTINGS_MANAGE,
  PERMISSIONS.ORG_MEMBERS_INVITE,
  PERMISSIONS.ORG_MEMBERS_MANAGE,
  PERMISSIONS.ORG_MEMBERS_REMOVE,
  PERMISSIONS.ORG_ROLES_MANAGE,
  PERMISSIONS.SITE_DELETE,
  PERMISSIONS.LICENCES_MANAGE,
  PERMISSIONS.AUDIT_VIEW,
];

const OWNER: Permission[] = [
  ...ADMIN,
  PERMISSIONS.ORG_DELETE,
  PERMISSIONS.ORG_OWNERSHIP_TRANSFER,
];

const uniq = (perms: Permission[]): Permission[] => [...new Set(perms)];

export interface SystemRoleDefinition {
  key: RoleKey;
  name: string;
  description: string;
  /** Lower = more powerful; used for "can this role manage that role" checks. */
  rank: number;
  permissions: Permission[];
}

export const SYSTEM_ROLES: SystemRoleDefinition[] = [
  { key: ROLE_KEYS.OWNER, name: 'Owner', description: 'Full control including billing and deletion', rank: 0, permissions: uniq(OWNER) },
  { key: ROLE_KEYS.ADMIN, name: 'Admin', description: 'Manage the organisation, team, sites and licences', rank: 1, permissions: uniq(ADMIN) },
  { key: ROLE_KEYS.DEVELOPER, name: 'Developer', description: 'Configure sites, environments, keys and webhooks', rank: 2, permissions: uniq(DEVELOPER) },
  { key: ROLE_KEYS.PUBLISHER, name: 'Publisher', description: 'Edit and publish content', rank: 3, permissions: uniq(PUBLISHER) },
  { key: ROLE_KEYS.EDITOR, name: 'Editor', description: 'Edit draft content and media, cannot publish', rank: 4, permissions: uniq(EDITOR) },
  { key: ROLE_KEYS.BILLING_MANAGER, name: 'Billing Manager', description: 'Manage subscription, invoices and payment methods', rank: 4, permissions: uniq(BILLING_MANAGER) },
  { key: ROLE_KEYS.VIEWER, name: 'Viewer', description: 'Read-only access', rank: 5, permissions: uniq(READ_ONLY) },
];

export const SYSTEM_ROLE_BY_KEY: Record<RoleKey, SystemRoleDefinition> = Object.fromEntries(
  SYSTEM_ROLES.map((role) => [role.key, role]),
) as Record<RoleKey, SystemRoleDefinition>;

export const PLATFORM_ROLES = {
  SUPER_ADMIN: 'super_admin',
  SUPPORT_ADMIN: 'support_admin',
} as const;

export type PlatformRole = (typeof PLATFORM_ROLES)[keyof typeof PLATFORM_ROLES];

/** API-key scopes map onto a subset of permissions. */
export const API_KEY_SCOPES = {
  CONTENT_READ: 'content:read',
  CONTENT_WRITE: 'content:write',
  REGISTRY_READ: 'registry:read',
  MEDIA_WRITE: 'media:write',
  PUBLISH: 'publish',
} as const;

export type ApiKeyScope = (typeof API_KEY_SCOPES)[keyof typeof API_KEY_SCOPES];

export const SCOPE_PERMISSIONS: Record<ApiKeyScope, Permission[]> = {
  [API_KEY_SCOPES.CONTENT_READ]: [PERMISSIONS.CONTENT_VIEW, PERMISSIONS.SITE_VIEW, PERMISSIONS.MEDIA_VIEW],
  [API_KEY_SCOPES.CONTENT_WRITE]: [PERMISSIONS.CONTENT_VIEW, PERMISSIONS.CONTENT_EDIT, PERMISSIONS.SITE_VIEW],
  [API_KEY_SCOPES.REGISTRY_READ]: [PERMISSIONS.SITE_VIEW, PERMISSIONS.CONTENT_VIEW],
  [API_KEY_SCOPES.MEDIA_WRITE]: [PERMISSIONS.MEDIA_VIEW, PERMISSIONS.MEDIA_MANAGE],
  [API_KEY_SCOPES.PUBLISH]: [PERMISSIONS.CONTENT_PUBLISH, PERMISSIONS.CONTENT_VIEW],
};

export const permissionsForScopes = (scopes: readonly string[]): Set<Permission> => {
  const set = new Set<Permission>();
  for (const scope of scopes) {
    const perms = SCOPE_PERMISSIONS[scope as ApiKeyScope];
    if (perms) for (const p of perms) set.add(p);
  }
  return set;
};
